// 用户侧文档展示
import React, { useEffect, useRef, useCallback } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import Styles from './index.module.scss';
import { DocType } from '@/pages/document/Types';
import { getHeaderList } from '@/pages/document/utils';
import { useHistory } from 'react-router-dom';
import { imageView } from '@byte-design/ui';
import { useCodeHightlight, useUrlQuery } from '@/utils/hooks/common';
import Marked from '@/component/Markdown/Plugin';
import ErrorReport from '../../ErrorReport';
import { ReactComponent as Loading } from '@/assets/doc-load.svg';
import ReactDom from 'react-dom';
import Survey from '../../Survey';
import SEO from '@/component/SEO';
import { getSEOConfig } from '@/pages/docs/config/seo';

interface Props{
  content: string;
  type: DocType;
  title?: string;
  status?: string;
  config: any;
  hashChange?: (hash: string) => void;
}

const UserDocView: React.FC<Props> = ({
  type, content, title, config,
  status = 'success', hashChange,
}) => {
  const history = useHistory();
  const lastContent = `${content}\n<div id="feelgood_container"></div>`;
  const ref = useRef<HTMLDivElement>(null);
  const feelgoodRef = useRef<HTMLDivElement>(null);
  const { doc_id: docId } = useUrlQuery();

  const seoConfig = getSEOConfig(docId || 'default');

  const State = useLocalStore(() => ({
    author: '',
  }));

  const handleCopyCodeClick = useCallback((self: HTMLElement) => () => {
    const active = self.parentElement?.getElementsByClassName('active')[0];
    self.setAttribute('data-clipboard-text', active?.textContent || (active as HTMLElement)?.innerText);
  }, []);

  const handleCodeLanguageClick = useCallback((slang: HTMLElement, slangs: HTMLCollectionOf<Element>) => () => {
    const lang = (slang as HTMLElement).innerText;
    for (let i = 0, pl = slangs.length; i < pl; i++) {
      slangs[i].setAttribute('class', 'header_item');
    }
    slang.setAttribute('class', 'header_item active');
    const codeBlock = slang?.parentElement?.nextElementSibling?.firstElementChild;
    if (codeBlock) {
      const codeBlockChild = codeBlock.children;
      const codeBlockActive = codeBlock.getElementsByClassName(`xcodeblock-${lang === 'curl' ? 'http' : lang.toLowerCase()}`)[0];
      for (let i = 0, pl = codeBlockChild.length; i < pl; i++) {
        codeBlockChild[i].classList.remove('active');
      }
      codeBlockActive.scrollTop = 0;
      codeBlockActive.classList.add('active');
    }
  }, []);

  const handleIndentClick = useCallback((index: number, self: HTMLElement) => () => {
    const list = self.parentNode?.parentNode?.parentNode?.parentNode?.children;
    if (!list) {
      return;
    }
    const len = list.length;
    const key = list[index].getAttribute('data-key');
    const isOpen = self.classList.contains('icon-unfold');
    if (isOpen) {
      self.setAttribute('class', 'iconfont icon-collapse');
    } else self.setAttribute('class', 'iconfont icon-unfold');
    for (let i = index + 1; i < len; i++) {
      const dataKey = list[i].getAttribute('data-key');
      if (dataKey && key && dataKey > key) {
        (list[i] as HTMLElement).style.display = isOpen ? 'none' : 'table-row';
        if (!isOpen) {
          const icon = list[i].getElementsByClassName('iconfont')[0];
          if (icon) {
            icon.className = 'iconfont icon-unfold';
          }
        }
      } else { return; }
    }
  }, []);

  /* eslint-disable  */
  const handleSideNavClick = useCallback((self: HTMLElement, idx: number) => () => {
    (window as any).isClickAuthor = true;
    const subling = self.parentNode?.children || [];
    for (let i = 0; i < subling.length; i++) {
      subling[i].classList.remove('active');
    }
    const icon = document.getElementById('author-sign-icon');
    if (icon) {
      icon.style.top = `${idx * 28}px`;
    }
    self.classList.add('active');
    setTimeout(() => {
      (window as any).isClickAuthor = false;
    }, 100);
  }, []);

  useEffect(() => {
    if (content && ref.current) {
      const container = ref.current as HTMLDivElement;
      container.scrollTop = 0;
      useCodeHightlight(container);
      State.author = getHeaderList(container, hashChange);
      ReactDom.render(<Survey />, document.getElementById('feelgood_container'));
    }
    // refactored from inline event handlers src/component/Markdown/Plugin/index.tsx
    // code block copy icon
    const codeBlockCopySvgs = document.getElementsByClassName('xcodeblock_cope_svg');
    for (let i = 0; i < codeBlockCopySvgs.length; i++) {
      const el = codeBlockCopySvgs.item(i);
      codeBlockCopySvgs.item(i)?.removeEventListener('click', handleCopyCodeClick(el as HTMLElement));
      codeBlockCopySvgs.item(i)?.addEventListener('mouseover', handleCopyCodeClick(el as HTMLElement));
    }

    // code block language header
    const codeBlockHeaders = document.getElementsByClassName('mk-xcodeblock-header');
    for (let i = 0; i < codeBlockHeaders.length; i++) {
      const header = codeBlockHeaders.item(i);
      const slangs = header?.getElementsByClassName('header_item');
      if (slangs) {
        for (let j = 0; j < slangs.length; j++) {
          const slang = slangs[j];
          slang.removeEventListener('click', handleCodeLanguageClick(slang as HTMLElement, slangs));
          slang.addEventListener('click', handleCodeLanguageClick(slang as HTMLElement, slangs));
        }
      }
    }

    // indent unfold/collapse icon
    const indentSpans = document.querySelectorAll('span[data-index]');
    for (let i = 0; i < indentSpans.length; i++) {
      const el = indentSpans[i];
      const index = Number(el.getAttribute('data-index') || '-1');
      if (index !== -1) {
        el.removeEventListener('click', handleIndentClick(index, el as HTMLElement));
        el.addEventListener('click', handleIndentClick(index, el as HTMLElement));
      }
    }

    // refactored from src/pages/document/utils/index.ts
    const sideNavLinks = document.querySelectorAll('.author-link-item');
    for (let i = 0; i < sideNavLinks.length; i++) {
      const el = sideNavLinks[i];
      el.removeEventListener('click', handleSideNavClick(el as HTMLElement, i));
      el.addEventListener('click', handleSideNavClick(el as HTMLElement, i));
    }
  }, [content]);

  useEffect(() => {
    const { hash } = history.location;
    if (hash && State.author) {
      setTimeout(() => {
        const activeAuthor = decodeURI(hash).replace('#', 'author-');
        const activeLink = decodeURI(hash).replace('#', 'a-');
        if (document.getElementsByClassName(activeAuthor)[0]) {
          const event = document.createEvent('MouseEvents');
          event.initEvent('click', false, true);
          (document.getElementsByClassName(activeAuthor)[0] as HTMLDivElement)!.dispatchEvent(event);
          (document.getElementsByClassName(activeLink)[0] as HTMLDivElement)!.dispatchEvent(event);
        }
      }, 0);
    }
  }, [State.author]);

  const renderErrorReport = useCallback(() => {
    if (ref.current && config?.athena?.formId) {
      return <ErrorReport containerRef={ref} id={docId} config={config.athena} />;
    }
    return null;
  }, [ref, config, docId]);

  const clickImageNode = useCallback((e) => {
    if (e.target.nodeName === 'IMG') {
      imageView({
        images: [{ url: e.target.src, title: e.target.title }],
        defaultActiveIndex: 0,
        scaleRatio: 0.25,
        maskClosable: true,
      });
    }
  }, []);

  /* eslint-disable react/no-danger */
  return (
    <>
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
      />
      <div className={`${Styles.container}`} onClick={clickImageNode}>
      {/* 站位 */}
      <div style={{ height: '20px', background: '#fff' }} />
      {/* 右边容器 */}
      <div className={Styles.sideBar}>
        <div ref={feelgoodRef} />
        {Boolean(content) && (
          <div
            dangerouslySetInnerHTML={{ __html: State.author }}
            className={Styles.author}
          />
        )}
      </div>
      {/* 内容 */}
      <div
        ref={ref}
        className={Styles.docContainer}
      >
        {/* 标题 */}
        {Boolean(title) && <div className={Styles.title}>{title}</div>}
        <div
          className={`${Styles.content} doc-content-body sg`}
          dangerouslySetInnerHTML={{ __html: type === 'RICH' ? lastContent : Marked.parse(lastContent) }}
        />
      </div>
      {/* 纠错提示 */}
      { renderErrorReport() }

      {/* loading model */}
      <div className={`${Styles.load} ${status === 'loading' && Styles.isShow}`}>
        <Loading />
      </div>
    </div>
    </>
  );
};

export default React.memo(observer(UserDocView));
